import { useMessages, useMessagesFromStart } from "@/lib/api/admin.ts";
import {
  MentorshipDetails,
  useUpdateMentorshipOverride,
} from "@/lib/api/mentorships.ts";
import { Header } from "@/components/mentorships/details/header.tsx";
import { MessageItem } from "@/components/mentorships/details/message-item.tsx";
import { InfiniteScroll } from "@/components/shared/infinite-scroll.tsx";
import { format } from "date-fns";
import { ChevronDown, ChevronUp, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button.tsx";
import {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { debounce, formatDate } from "@/lib/utils.ts";
import { useChatOverride } from "@/lib/hooks/conversations/useChatOverride";
import {
  CATEGORY_LABELS,
  PRIORITY_CONFIG,
} from "@/components/risk-flagging/constants";
import { ChatEntityFeatureFlagsProvider } from "@/components/shared/chat-feature-flags-context";
import type { ChatConversationMember } from "@/components/shared/chat-message-types";

interface ConversationProps {
  mentorshipId: string;
  mentorship: MentorshipDetails;
}

export function Conversation({ mentorshipId, mentorship }: ConversationProps) {
  const [viewMode, setViewMode] = useState<"recent" | "first">("recent");
  const conversationMembers: ChatConversationMember[] = useMemo(
    () => [
      {
        id: mentorship.mentor.id,
        name: mentorship.mentor.name,
        avatarUrl: mentorship.mentor.profile_picture_url,
        systemRoles: mentorship.mentor.systemRoles,
      },
      {
        id: mentorship.mentee.id,
        name: mentorship.mentee.name,
        avatarUrl: mentorship.mentee.profile_picture_url,
        systemRoles: mentorship.mentee.systemRoles,
      },
    ],
    [mentorship],
  );

  const recentMessagesQuery = useMessages(mentorshipId);
  const firstMessagesQuery = useMessagesFromStart(
    mentorshipId,
    viewMode === "first",
  );

  const activeQuery =
    viewMode === "recent" ? recentMessagesQuery : firstMessagesQuery;
  const {
    data,
    isPending: isMessagesPending,
    isError: isMessagesError,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  } = activeQuery;

  const messages = data?.pages.flatMap((page) => page.messages) ?? [];

  const handleLoadMore = useCallback(() => {
    fetchNextPage();
  }, [fetchNextPage]);

  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [showChevron, setShowChevron] = useState(true);
  const hideTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleViewModeToggle = useCallback(() => {
    setViewMode((prev) => (prev === "recent" ? "first" : "recent"));
    // Reset visibility + auto-hide timer on toggle
    setShowChevron(true);
    if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
    hideTimerRef.current = setTimeout(() => setShowChevron(false), 5000);
  }, []);

  // Auto-hide chevron after 5 seconds of inactivity, reset on scroll
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const resetTimer = () => {
      setShowChevron(true);
      if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
      hideTimerRef.current = setTimeout(() => setShowChevron(false), 5000);
    };

    // Start initial timer
    resetTimer();

    container.addEventListener("scroll", resetTimer, { passive: true });
    return () => {
      container.removeEventListener("scroll", resetTimer);
      if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
    };
  }, [viewMode, isMessagesPending]);

  // Auto-switch chevron direction when reaching the end of loaded messages
  // and there are no more pages to fetch
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const handleScrollEnd = () => {
      if (hasNextPage || isFetchingNextPage) return;

      const { scrollTop, scrollHeight, clientHeight } = container;
      const threshold = 50;

      if (viewMode === "recent") {
        // flex-col-reverse: scrolled all the way up = negative scrollTop near max
        const distanceFromTop =
          scrollHeight - clientHeight - Math.abs(scrollTop);
        if (distanceFromTop < threshold) {
          setViewMode("first");
        }
      } else {
        // flex-col: scrolled all the way down
        const distanceFromBottom = scrollHeight - clientHeight - scrollTop;
        if (distanceFromBottom < threshold) {
          setViewMode("recent");
        }
      }
    };

    container.addEventListener("scroll", handleScrollEnd, { passive: true });
    return () => container.removeEventListener("scroll", handleScrollEnd);
  }, [viewMode, hasNextPage, isFetchingNextPage]);

  // @todo - figure out issue with this
  const [, setCurrentDate] = useState<string | null>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);
  const messageRefs = useRef<Array<HTMLElement | null>>([]);

  const windowProxy = useRef<WindowProxy | null>(null);
  const updateOverride = useUpdateMentorshipOverride(mentorshipId);
  const { isOverridden, overriddenByName } = useChatOverride(mentorshipId);

  const debounceUpdateDate = useCallback(
    debounce((date) => setCurrentDate(date), 200),
    [],
  );

  const handleChatTakeover = () => {
    if (!mentorship) return;

    const width = 500;
    const height = 800;
    const left = (window.screen.width - width) / 2;
    const top = (window.screen.height - height) / 2;

    updateOverride.mutateAsync(true);

    windowProxy.current = window.open(
      `/chat/${mentorship.id}`,
      "Chat Takeover",
      `width=${width},height=${height},left=${left},top=${top}`,
    );
  };

  const handleChatClose = () => {
    updateOverride.mutateAsync(false);
    if (windowProxy.current) windowProxy.current.close();
  };

  useEffect(() => {
    if (observerRef.current) {
      observerRef.current.disconnect();
    }

    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const messageDate = formatDate(
              new Date(+(entry.target as any).dataset.createdAt),
            );
            debounceUpdateDate(messageDate);
          }
        });
      },
      { threshold: 0.1 },
    );

    messageRefs.current.forEach((ref) => {
      if (ref && observerRef.current) observerRef.current.observe(ref);
    });

    return () => {
      if (observerRef.current) observerRef.current.disconnect();
    };
  }, [messages]);

  return (
    <ChatEntityFeatureFlagsProvider>
      <div className="relative h-full">
        <Header mentorship={mentorship} />

        {isMessagesPending ? (
          <div className="flex flex-1 items-center justify-center">
            <Loader2 className="h-12 w-12 animate-spin" />
          </div>
        ) : !messages || isMessagesError ? (
          <div className="flex flex-1 items-center justify-center">
            <div className="">Some errors occurred!</div>
          </div>
        ) : (
          <>
            <div
              ref={scrollContainerRef}
              className={`flex overflow-y-auto px-2 py-2 pb-24 ${viewMode === "recent" ? "flex-col-reverse" : "flex-col"}`}
              style={{ height: "calc(100vh - 70px)", overflowAnchor: "auto" }}
            >
              {messages.map((message, idx) => {
                const isMentor =
                  message?.user?.user_id === mentorship.mentor.id;
                const previousMessageDate =
                  viewMode === "recent"
                    ? idx === messages.length - 1
                      ? null
                      : format(messages[idx + 1]?.created_at, "yyyy-MM-dd")
                    : idx === 0
                      ? null
                      : format(messages[idx - 1]?.created_at, "yyyy-MM-dd");
                const currentDate = format(message.created_at, "yyyy-MM-dd");
                const shouldShowDate = previousMessageDate !== currentDate;

                const isFlagged = !!message.riskFlag;
                const flagPriority = message.riskFlag?.priority ?? "STANDARD";
                const flagConfig =
                  PRIORITY_CONFIG[flagPriority] ?? PRIORITY_CONFIG.STANDARD;
                const flagLabel = isFlagged
                  ? CATEGORY_LABELS[message.riskFlag!.category] ??
                    message.riskFlag!.category
                  : null;

                const borderColor =
                  flagConfig.rowBorder.split(" ")[1] ?? "border-zinc-400";
                const bgColor = flagConfig.rowBg || "bg-zinc-50";
                const textColor =
                  flagPriority === "HIGH"
                    ? "text-red-600"
                    : flagPriority === "MEDIUM"
                      ? "text-orange-500"
                      : "text-zinc-500";
                const badgeBg = flagConfig.pillBg;

                return (
                  <Fragment key={message.message_id}>
                    <div
                      ref={(el) => (messageRefs.current[idx] = el)}
                      data-created-at={message.created_at}
                      className={
                        isFlagged
                          ? `my-1 rounded-lg border-l-4 px-4 py-3 ${borderColor} ${bgColor}`
                          : undefined
                      }
                    >
                      {isFlagged && flagLabel && (
                        <p
                          className={`mb-2 flex items-center gap-1.5 text-xs font-semibold ${textColor}`}
                        >
                          <span
                            className={`flex h-5 w-5 items-center justify-center rounded-full text-[10px] text-white ${badgeBg}`}
                          >
                            !
                          </span>
                          {flagConfig.label} – {flagLabel}
                        </p>
                      )}
                      <MessageItem
                        message={message}
                        showOnRightSide={isMentor}
                        isHighlighted={isFlagged}
                        conversationMembers={conversationMembers}
                      />
                    </div>
                    {shouldShowDate && (
                      <div className="mx-auto mb-2 w-[200px] rounded-full bg-gray-200 p-1 text-center text-xs text-gray-800">
                        {formatDate(message.created_at)}
                      </div>
                    )}
                  </Fragment>
                );
              })}

              <InfiniteScroll
                onLoadMore={handleLoadMore}
                hasMore={!!hasNextPage}
                isLoading={isFetchingNextPage}
                root={scrollContainerRef}
                showLoader
                className="py-2"
              />
            </div>

            <Button
              variant="outline"
              size="sm"
              className={`absolute left-1/2 top-20 z-10 -translate-x-1/2 cursor-pointer rounded-full shadow-md transition-opacity duration-300 ${showChevron ? "opacity-100" : "pointer-events-none opacity-0"}`}
              onClick={handleViewModeToggle}
            >
              {viewMode === "recent" ? (
                <ChevronUp className="h-4 w-4" />
              ) : (
                <ChevronDown className="h-4 w-4" />
              )}
            </Button>

            <div className="absolute bottom-0 left-0 right-0">
              <div className="flex w-full justify-center bg-white/30 p-4 backdrop-blur-sm">
                {!isOverridden && (
                  <Button
                    onClick={handleChatTakeover}
                    className="w-[200px] bg-black hover:bg-gray-700"
                  >
                    Override Mentor
                  </Button>
                )}

                {isOverridden && (
                  <div className="flex flex-row items-center p-1">
                    <p className="text-center text-sm font-semibold text-gray-800">
                      Overridden by {overriddenByName}
                    </p>
                    <Button
                      onClick={handleChatClose}
                      variant="ghost"
                      className="ml-2"
                    >
                      End Override
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </ChatEntityFeatureFlagsProvider>
  );
}
